import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { TableHead } from '@/components/ui/table';
import type { SortDirection } from '@/hooks/useTableSort';
import { cn } from '@/lib/utils';

interface SortableTableHeadProps {
  children: React.ReactNode;
  sortKey?: string;
  currentSortDirection?: SortDirection;
  onSort?: () => void;
  className?: string;
  align?: 'left' | 'center' | 'right';
}

/**
 * Sortable table header component with visual indicators
 * Supports three-state sorting: null -> asc -> desc -> null
 */
export function SortableTableHead({
  children,
  sortKey,
  currentSortDirection,
  onSort,
  className,
  align = 'left',
}: SortableTableHeadProps) {
  const isSortable = !!onSort;
  const isSorted = currentSortDirection !== null && currentSortDirection !== undefined;

  const alignmentClass = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  }[align];

  const getSortIcon = () => {
    if (!isSortable) return null;

    if (currentSortDirection === 'asc') {
      return <ArrowUp className="ml-2 h-4 w-4" />;
    }
    if (currentSortDirection === 'desc') {
      return <ArrowDown className="ml-2 h-4 w-4" />;
    }
    return <ArrowUpDown className="ml-2 h-4 w-4 opacity-40" />;
  };

  if (!isSortable) {
    return <TableHead className={cn(alignmentClass, className)}>{children}</TableHead>;
  }

  return (
    <TableHead className={cn(alignmentClass, className)}>
      <button
        type="button"
        onClick={onSort}
        className={cn(
          'flex items-center gap-1 font-medium transition-colors hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm px-1 -mx-1',
          isSorted ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
          align === 'right' && 'ml-auto',
          align === 'center' && 'mx-auto',
        )}
        aria-label={`Sort by ${sortKey || children}`}
        aria-sort={
          currentSortDirection === 'asc' ? 'ascending' : currentSortDirection === 'desc' ? 'descending' : 'none'
        }
      >
        {children}
        {getSortIcon()}
      </button>
    </TableHead>
  );
}
